import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { scheduleOnce } from '@ember/runloop';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { Slack, ValidationErrors } from 'webapp-types';
import { ScheduleType, SnapshotFormat } from 'webapp/enums/enums';

import didUpdate from 'webapp/modifiers/did-update';
import BoardsShareScheduleAdvancedSettingsComponent from 'webapp/components/boards/share/schedule/advanced-settings';
import FormEmailAndSlackRecipientsComponent from 'webapp/components/form/email-and-slack-recipients';
import FormRadiobuttonGroupComponent from 'webapp/components/form/radiobutton-group';
import FormRadiobuttonSingleComponent from 'webapp/components/form/radiobutton-single';
import FormRangeDropdownComponent from 'webapp/components/form/range/dropdown';
import FormScheduleComponent from 'webapp/components/form/schedule';

import type BoardShare from 'webapp/models-custom/board-share';
import type DateRange from 'webapp/models-custom/date-range';

interface Args {
	model: BoardShare;
	isPeriodic: boolean;
	trackingOrigin: string;
	submitInProgress: boolean;
	errors?: ValidationErrors<string>;
	onContentChange?: () => void;
	theme?: string;
}

const enum DateRangeOption {
	GENERAL = 1,
	SPECIFIC = 2,
}

export default class BoardsShareScheduleFormComponent extends Component<Args> {
	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.resetForm();
		this.triggerContentChange();
	}

	get formatValues() {
		const isBoard = !!this.args.model.boardId;
		const icon: IconName = 'InfoFill';
		return [
			{
				label: 'JPG',
				value: SnapshotFormat.JPG,
				tooltip:
					'The file format: JPG will send each dashboard as a separate JPG attachment in the scheduled email.',
				tooltipIcon: icon,
			},
			{
				label: 'PDF',
				value: SnapshotFormat.PDF,
				tooltip: isBoard
					? 'The file format: PDF will send each dashboard as a one-page PDF attachment in the scheduled email.'
					: 'The file format: PDF will combine dashboards in a multi-page PDF that will be attached to the scheduled email.',
				tooltipIcon: icon,
			},
		];
	}

	get recipientsTrackingOrigin() {
		return this.args.isPeriodic ? 'scheduled_snapshot' : 'send_now';
	}

	@tracked dateRangeRadiobutton = DateRangeOption.GENERAL;

	get isDateRangeRadiobuttonGeneralSelected() {
		return this.dateRangeRadiobutton === DateRangeOption.GENERAL;
	}

	get isDateRangeRadiobuttonSpecificSelected() {
		return this.dateRangeRadiobutton === DateRangeOption.SPECIFIC;
	}

	get addSendToTextToLabels() {
		return !this.args.isPeriodic;
	}

	@action
	modelChanged() {
		scheduleOnce('afterRender', this, this.resetForm);
	}

	resetForm() {
		const model = this.args.model;
		if (!this.args.isPeriodic) {
			model.scheduleType = ScheduleType.ONE_OFF;
		}
		model.updateScheduleValues();
		this.dateRangeRadiobutton =
			!model.isNew && model.dateRange?.range
				? DateRangeOption.SPECIFIC
				: DateRangeOption.GENERAL;
	}

	@action
	triggerContentChange() {
		const fn = this.args.onContentChange;
		if (fn) {
			scheduleOnce('afterRender', this, fn);
		}
	}

	@action
	changeFormat(format: AnySimpleValue) {
		this.args.model.format = format as SnapshotFormat;
	}

	@action
	changeDateRangeRadiobutton(value: AnySimpleValue) {
		this.dateRangeRadiobutton = value as DateRangeOption;
		this.changeDateRange(
			this.isDateRangeRadiobuttonGeneralSelected
				? null
				: this.args.model.metricLimitations.dateRange,
		);
	}

	@action
	changeDateRange(dateRange: DateRange | null) {
		const model = this.args.model;
		if (dateRange) {
			model.metricLimitations.updateDateRangeFromOnlyRangeFormula(dateRange.rangeFormula);
			model.dateRange = { range: dateRange.rangeFormula };
		} else {
			model.dateRange = {};
		}
	}

	@action
	changeSlackChannels(value: Slack.Channels) {
		this.args.model.slackChannels = value;
	}
	<template>
		<FormEmailAndSlackRecipientsComponent
			@model={{@model}}
			@origin={{this.recipientsTrackingOrigin}}
			@errors={{@errors}}
			@submitInProgress={{@submitInProgress}}
			@onContentChange={{this.triggerContentChange}}
			@addSendToTextToLabels={{this.addSendToTextToLabels}}
			@theme={{@theme}}
		/>

		{{#if @isPeriodic}}
			<FormScheduleComponent
				@model={{@model}}
				@showOneOff={{false}}
				@submitInProgress={{@submitInProgress}}
				@theme={{@theme}}
			/>
		{{/if}}

		<div class="row row--radiobuttons row--mb-less" {{didUpdate this.modelChanged @model.id}}>
			<label>File format</label>
			<div class="right">
				<FormRadiobuttonGroupComponent
					class="radiobutton-default"
					@name="format"
					@selected={{@model.format}}
					@options={{this.formatValues}}
					@isRequired={{true}}
					@onChange={{this.changeFormat}}
				/>
			</div>
		</div>

		<BoardsShareScheduleAdvancedSettingsComponent
			@model={{@model}}
			@isPeriodic={{@isPeriodic}}
			@showShareableLink={{true}}
			@submitInProgress={{@submitInProgress}}
			@onContentChange={{this.triggerContentChange}}
		>
			<div class="row row--radiobuttons row--radiobuttons-and-dropdowns">
				<label>Date range</label>
				<div class="right">
					<div class="radiobutton-default radiobutton-group size-normal">
						<span class="radiobutton size-normal val-default">
							<FormRadiobuttonSingleComponent
								@id="dr-rb-1"
								@name="dr-rb"
								@value={{1}}
								@selected={{this.dateRangeRadiobutton}}
								@onChange={{this.changeDateRangeRadiobutton}}
							/>
							<label for="dr-rb-1">
								As set on dashboard(s)
							</label>
						</span>
						<span class="radiobutton size-normal val-input">
							<FormRadiobuttonSingleComponent
								@id="dr-rb-2"
								@name="dr-rb"
								@value={{2}}
								@selected={{this.dateRangeRadiobutton}}
								@onChange={{this.changeDateRangeRadiobutton}}
							/>
							<label for="dr-rb-2">
								<FormRangeDropdownComponent
									@metricLimitations={{@model.metricLimitations}}
									@isDisabled={{this.isDateRangeRadiobuttonGeneralSelected}}
									@onChange={{this.changeDateRange}}
									@theme={{@theme}}
								/>
							</label>
						</span>
					</div>
				</div>
			</div>
		</BoardsShareScheduleAdvancedSettingsComponent>
	</template>
}
